import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useCancelCoachBooking, useCancelCourtBooking } from '../../hooks/useAPI';
import supabase from '../../lib/supabase';

interface CancelBookingModalProps {
  kind: 'court' | 'coach';
  bookingId: string;
  bookingReference: string;
  startAt: string;
  totalAmount: number;
  currency: string;
  withCredit?: boolean;     // coach booking redeemed from a package
  clubId?: string;          // when present, the club's per-policy overrides the defaults
  onClose: () => void;
  onDone: () => void;
}

type Policy = {
  fullHours: number;
  halfHours: number;
  fee: number;
};
const DEFAULT_POLICY: Policy = { fullHours: 24, halfHours: 12, fee: 20 };

export default function CancelBookingModal({
  kind, bookingId, bookingReference, startAt, totalAmount, currency, withCredit, clubId,
  onClose, onDone,
}: CancelBookingModalProps) {
  const court = useCancelCourtBooking();
  const coach = useCancelCoachBooking();
  const busy = court.isLoading || coach.isLoading;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [policy, setPolicy] = useState<Policy>(DEFAULT_POLICY);

  // Pull the per-club cancellation policy so the preview matches what
  // the edge function will actually refund.
  useEffect(() => {
    if (!clubId) { setPolicy(DEFAULT_POLICY); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('clubs')
        .select('cancel_full_cutoff_hours, cancel_half_cutoff_hours, cancel_processing_fee')
        .eq('id', clubId).maybeSingle();
      if (cancelled || !data) return;
      setPolicy({
        fullHours: Number(data.cancel_full_cutoff_hours ?? 24),
        halfHours: Number(data.cancel_half_cutoff_hours ?? 12),
        fee:       Number(data.cancel_processing_fee    ?? 20),
      });
    })();
    return () => { cancelled = true; };
  }, [clubId]);

  const hoursUntilStart = useMemo(() => {
    const ms = new Date(startAt).getTime() - Date.now();
    return ms / 3_600_000;
  }, [startAt]);

  const estimate = useMemo(() => {
    if (withCredit) {
      return hoursUntilStart >= policy.fullHours
        ? { percent: 100, amount: 0, note: 'Credit returned to your wallet.' }
        : { percent: 0, amount: 0, note: `Credit forfeited (<${policy.fullHours}h notice).` };
    }
    if (hoursUntilStart >= policy.fullHours) {
      const fee = kind === 'court' ? policy.fee : 0;
      return { percent: 100, amount: Math.max(0, totalAmount - fee), note: fee
        ? `Full refund minus AED ${fee} processing fee.`
        : 'Full refund.' };
    }
    if (hoursUntilStart >= policy.halfHours) {
      return { percent: 50, amount: totalAmount * 0.5,
        note: `50% refund (${policy.halfHours}–${policy.fullHours} h notice).` };
    }
    return { percent: 0, amount: 0,
      note: `No refund for cancellations under ${policy.halfHours} hours before start.` };
  }, [hoursUntilStart, totalAmount, kind, withCredit, policy]);

  const submit = async () => {
    setError(null);
    const fn = kind === 'court' ? court.cancel : coach.cancel;
    const { data, error } = await fn(bookingId, reason || undefined);
    if (error) { setError(error.message || 'Could not cancel'); return; }
    setResult(data);
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
        <div className="bg-background rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-6">
          <div className="w-14 h-14 rounded-full bg-[#D4F04A] flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-7 h-7 text-[#1A6B4F]" />
          </div>
          <h2 className="text-lg font-semibold text-center">Booking cancelled</h2>
          <p className="text-sm text-muted-foreground text-center mt-2">
            {result.message}
          </p>
          {result.refundAmount > 0 && (
            <p className="text-sm text-center mt-1">
              Refund: <span className="font-medium text-foreground">{currency} {Number(result.refundAmount).toFixed(0)}</span>
            </p>
          )}
          <button
            onClick={onDone}
            className="mt-5 w-full h-11 rounded-xl bg-[#1A6B4F] text-white text-sm"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-background rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Cancel booking?</h2>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">{bookingReference}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-card border border-border rounded-xl p-3 mb-3">
          <div className="text-xs text-muted-foreground">
            Starts in {Math.round(hoursUntilStart * 10) / 10}h
          </div>
          <div className="text-sm mt-1">{estimate.note}</div>
          {!withCredit && estimate.amount > 0 && (
            <div className="mt-1 text-sm text-[#1A6B4F] font-medium">
              Est. refund: {currency} {estimate.amount.toFixed(0)}
            </div>
          )}
        </div>

        <label className="block mb-3">
          <div className="text-xs text-muted-foreground mb-1">Reason (optional)</div>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={200}
            placeholder="Something came up…"
            className="w-full px-3 py-2 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none text-sm resize-none"
          />
        </label>

        {error && (
          <div className="mb-3 bg-destructive/10 border border-destructive/20 rounded-xl p-3 flex gap-2 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-border hover:bg-secondary text-sm">
            Keep booking
          </button>
          <button
            onClick={submit} disabled={busy}
            className="flex-1 h-11 rounded-xl bg-destructive text-destructive-foreground text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Cancel booking
          </button>
        </div>
      </div>
    </div>
  );
}
